<template>
  <div>
    <div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="$emit('close')"
      >
        ×
      </button>
    </div>
    <PayerHeader
      v-bind="order.payer"
      class="pt-4 pb-3"
    />
    <div
      v-for="c in columnsWithNames"
      :key="c.key"
      style="min-height: 25px"
      class="d-flex align-items-center my-2"
    >
      <div
        style="width: 180px"
        class="small text-secondary"
      >
        <i :class="c.icon" />
        {{ c.name }}
      </div>

      <span
        v-if="c.key === 'productSetName'"
        :class="c.key"
      >
        {{ order.set.name }}
      </span>

      <div
        v-else-if="c.key === 'day'"
        class="money-font"
      >
        {{ singleOrderSchema ? "" : order.formattedDay }}
      </div>
      <div
        v-else-if="c.key === 'month'"
        class="money-font"
      >
        {{ order.formattedMonth }}
      </div>

      <span
        v-else-if="c.key === 'status'"
        :class="c.key"
        class="status-name"
      >
        <OrderFieldDropdown
          :options="statusOptions"
          style="margin-left: -7px"
          :selected.sync="updatedData.orderStatusId"
          :modified="updatedData.orderStatusId !== initData.orderStatusId"
        >
          <template #option="{ scope }">
            <span
              v-if="scope"
              class="badge px-3"
              :class="scope.value ? 'text-white' : ''"
              :style="{ backgroundColor: scope.value ? colors(scope.color) : 'transparent' }"
            >
              {{ scope.text }}
            </span>
            <span v-else>
              {{ '-' }}
            </span>
          </template>
        </OrderFieldDropdown>
      </span>

      <span
        v-else-if="c.key === 'created'"
        :class="c.key"
      >
        {{ formatDate(order.created) }}
      </span>

      <div
        v-else-if="c.key === 'paid'"
        :class="c.key"
      >
        <OrderFieldDropdown
          :options="yesNoOptions"
          :selected.sync="updatedData.isPaid"
          :modified="updatedData.isPaid !== initData.isPaid"
          style="margin-left: -7px"
        >
          <template #option="{ scope }">
            <div
              v-if="scope && scope.value"
              class="text-success small text-uppercase"
            >
              <span
                class="fa-stack fa-1x"
                style="width: 18px"
              >
                <i class="far fa-circle-dollar fa-stack-1x" />
              </span>

              {{ $t('general.yes') }}
            </div>
            <div
              v-else
              class="text-danger small text-uppercase"
            >
              <span
                class="fa-stack fa-1x"
                style="width: 18px"
              >
                <i class="far fa-circle-dollar fa-stack-1x" />
                <i class="far fa-slash fa-stack-1x" />
              </span>
              <span>
                {{ $t('general.no') }}
              </span>
            </div>
          </template>
        </OrderFieldDropdown>
      </div>
      <div
        v-else-if="c.key === 'delivered'"
        :class="c.key"
      >
        <OrderFieldDropdown
          :options="yesNoOptions"
          :selected="updatedData.delivered"
          :modified="updatedData.delivered !== initData.delivered"
          style="margin-left: -7px"
          @update:selected="updateDelivered"
        >
          <template #option="{ scope }">
            <div
              v-if="scope.value"
              class="text-success small text-uppercase"
            >
              <span
                class="fa-stack fa-1x"
                style="width: 18px"
              >
                <i class="far fa-hand-holding-box fa-stack-1x" />
              </span>

              {{ $t('general.yes') }}
              <span v-if="updatedData.delivered">
                ({{ updatedData.deliveredCount }})
              </span>
            </div>
            <div
              v-else
              class="text-danger small text-uppercase"
            >
              <span
                class="fa-stack fa-1x"
                style="width: 18px"
              >
                <i class="far fa-hand-holding-box fa-stack-1x" />
                <i class="far fa-slash fa-stack-1x" />
              </span>
              <span>
                {{ $t('general.no') }}
              </span>
            </div>
          </template>
        </OrderFieldDropdown>
      </div>
      <div
        v-else-if="c.key === 'count'"
        :class="c.key"
        class="money-font"
      >
        <OrderFieldText
          v-model="updatedData.count"
          :modified="updatedData.count !== initData.count"
          style="margin-left: -7px"
        />
      </div>
      <div
        v-else-if="c.key === 'price'"
        :class="c.key"
        class="money-font"
      >
        <div v-if="subscription">
          {{ formatCurrency(updatedData.unitPrice, order.currency) }}
        </div>
        <OrderFieldText
          v-else
          ref="unitPriceInput"
          v-model="updatedData.unitPrice"
          :modified="updatedData.unitPrice !== initData.unitPrice"
          style="margin-left: -7px"
        >
          {{ formatCurrency(updatedData.unitPrice, order.currency) }}

          <template #edit>
            <MoneyInput
              v-model="updatedData.unitPrice"
              auto-select
              :currency="order.currency"
              size="sm"
              class="money-input"
              @blur="moneyBlur"
            />
          </template>
        </OrderFieldText>
      </div>
      <div
        v-if="c.key === 'discount'"
        :class="c.key"
      >
        <OrderFieldText
          v-model="updatedData.discountName"
          :modified="updatedData.discountName !== initData.discountName"
          style="margin-left: -7px"
          expandable
        />
      </div>
      <div
        v-else-if="c.key === 'comment'"
        :class="c.key"
      >
        <OrderFieldText
          v-model="updatedData.comment"
          :modified="updatedData.comment !== initData.comment"
          style="margin-left: -7px"
          expandable
        />
      </div>
    </div>

    <div
      v-if="hasOrderFormData"
      class="mt-3"
    >
      <hr>
      <div class="small text-secondary mb-2">
        <i class="far fa-clipboard-list-check mr-1" />
        {{ $t('shop.subscriptionFormDetails') }}
      </div>
      <div
        v-for="field in subscriptionFormFields"
        :key="field.id || field.code"
        class="d-flex align-items-start my-2"
      >
        <div
          style="width: 180px"
          class="small text-secondary"
        >
          {{ fieldLabel(field) }}
        </div>
        <div class="small flex-grow-1">
          <div
            v-if="field.type !== 'fileinput'"
            class="form-value"
          >
            {{ fieldValue(field) }}
          </div>
          <div
            v-else
            class="form-value"
          >
            <a
              v-if="fieldValue(field)"
              :href="uploadedUrl(fieldValue(field))"
              target="_blank"
              rel="noopener noreferrer"
              class="small mb-1 field-attachment-link"
            >
              <i class="far fa-file-alt mr-1" />
              {{ uploadedName(fieldValue(field)) }}
            </a>
            <span v-else>
              <i class="far fa-file-alt mr-1" />
              brak załącznika
            </span>
          </div>
          <div
            v-if="fieldAttachments(field).length > 0"
            class="mt-1 d-flex flex-column"
          >
            <a
              v-for="attachment in fieldAttachments(field)"
              :key="attachment.key"
              :href="attachment.url"
              target="_blank"
              rel="noopener noreferrer"
              class="small mb-1 field-attachment-link"
            >
              <i class="far fa-file-alt mr-1" />
              {{ attachment.name || fileNameFromKey(attachment.key) }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <hr>
    <div
      class="d-flex justify-content-between"
    >
      <Tippy
        ref="archive-popup"
        trigger="click"
        theme="light"
        arrow
        interactive
      >
        <template #trigger>
          <div
            class="secondary-icon bill-button px-3"
          >
            <i class="fas fa-ban mr-2" />
            <span class="edit-details">
              {{ $t('shop.cancel') }}
            </span>
          </div>
        </template>

        <button
          class="btn btn-primary btn-sm btn-block"
          :disabled="pendingCancel"
          @click="subscription ? requestRemoveSubscription() : cancelOrder()"
        >
          <i class="fas fa-ban pr-2" />
          {{ $t('shop.cancel') }}
        </button>
      </Tippy>

      <div
        v-if="modified > 0"
        class="d-flex justify-content-end"
      >
        <button
          class="btn btn-sm bill-button btn-secondary mx-1"
          :disabled="pendingUpdate"
          @click="reset()"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          class="btn btn-sm bill-button btn-primary mx-1"
          :disabled="pendingUpdate"
          @click="update"
        >
          {{ $t('general.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PayerHeader from '@/components/payers/PayerHeader';
import OrderFieldDropdown from '@/components/shop/report/table/OrderFieldDropdown';
import OrderFieldText from '@/components/shop/report/table/OrderFieldText';
import MoneyInput from '@/components/utils/MoneyInput';
import ordersColumns from '@/pages/shop/order/columns';
import colors from '@/utils/colors';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    order: Object,
    subscription: Boolean,
  },
  data: () => ({
    ordersColumns,
    updatedData: {},
    initData: {},
    pendingCancel: false,
    pendingUpdate: false,
  }),
  components: {
    PayerHeader,
    OrderFieldDropdown,
    MoneyInput,
    OrderFieldText,
  },
  computed: {
    ...mapGetters(['formatCurrency']),
    ...mapGetters('shop', [
      'singleOrderSchema',
      'catalogDetails',
    ]),
    statusOptions() {
      return [
        {
          text: '-',
          value: '00000000-0000-0000-0000-000000000000',
          color: '#aaa',
        },
        ...(this.catalogDetails.orderStatuses
          .map((s) => ({
            text: s.name,
            color: s.color,
            value: s.id,
          }))),
      ];
    },
    statusName() {
      return this.catalogDetails.orderStatuses
        .find((s) => s.id === this.order.orderStatusId)?.name;
    },
    yesNoOptions() {
      return [
        {
          text: this.$t('general.yes'),
          value: true,
        },
        {
          text: this.$t('general.no'),
          value: false,
        },
      ];
    },
    columnsWithNames() {
      if (this.subscription) {
        const idColumns = ['productSetName', 'created', 'price'];
        return this.ordersColumns
          .filter((x) => idColumns.includes(x.key));
      }
      if (this.editMode) {
        const idColumns = ['productSetName', 'day', 'created'];

        return this.ordersColumns
          .filter((x) => idColumns.includes(x.key));
      }
      return this.ordersColumns
        .filter((c) => c.key !== 'day' || this.catalogDetails.orderSchema === 'Daily')
        .filter((c) => c.key !== 'name' && c.key !== 'code')
        .filter((c) => c.key !== 'vat' && c.key !== 'accountingCode' && c.key !== 'accountNumber')
        .filter((c) => c.key !== 'status' || this.catalogDetails.orderStatuses.length > 0);
    },
    modified() {
      return Object.keys(this.updatedData)
        .filter((k) => this.updatedData[k] !== this.initData[k])
        .length;
    },
    subscriptionFields() {
      return this.order?.fields || this.order?.Fields || {};
    },
    subscriptionFormLayout() {
      const layout = this.order?.formLayout || this.order?.FormLayout;
      if (!layout) return [];

      try {
        const parsed = JSON.parse(layout);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },
    subscriptionFormFields() {
      return this.subscriptionFormLayout
        .filter((f) => f?.type !== 'statictext' && !!f?.code);
    },
    subscriptionAttachments() {
      const attachments = this.order?.attachments || this.order?.Attachments;
      return Array.isArray(attachments) ? attachments : [];
    },
    hasOrderFormData() {
      return this.subscriptionFormFields.length > 0
        || Object.keys(this.subscriptionFields).length > 0
        || this.subscriptionAttachments.length > 0;
    },
  },
  methods: {
    ...mapActions('shop', [
      'updateOrderItems',
      'updateOrders',
      'removeSubscriptions',
    ]),
    uploadedUrl(key) {
      return this.order.uploads[key];
    },
    uploadedName(key) {
      if (!key) return '';
      return this.order.uploadNames?.[key] || this.fileNameFromKey(key);
    },
    moneyBlur() {
      if (this.$refs.unitPriceInput[0]) {
        this.$refs.unitPriceInput[0].blur();
      }
    },
    updateDelivered(v) {
      if (v) {
        this.updatedData.delivered = true;
        this.updatedData.deliveredCount = this.order.set.productIds.length;
      } else {
        this.updatedData.delivered = false;
        this.updatedData.deliveredCount = 0;
      }
    },
    colors,
    update() {
      this.pendingUpdate = true;
      this.updateOrderItems({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: [{
            payerId: this.order.payerId,
            orderId: this.order.orderId,
            date: this.order.day,
            offerId: this.order.availabilityId,
          }],
          statusId: this.updatedData.orderStatusId === this.initData.orderStatusId
            ? null : this.updatedData.orderStatusId,
          price: this.updatedData.unitPrice === this.initData.unitPrice
            ? null : this.updatedData.unitPrice,
          count: this.updatedData.count === this.initData.count
            ? null : this.updatedData.count,
          isPaid: this.updatedData.isPaid === this.initData.isPaid
            ? null : this.updatedData.isPaid,
          unitPrice: this.updatedData.unitPrice === this.initData.unitPrice
            ? null : this.updatedData.unitPrice,
          comment: this.updatedData.comment === this.initData.comment
            ? null : this.updatedData.comment,
          discount: this.updatedData.discountName === this.initData.discountName
            ? null : this.updatedData.discountName,
          delivered: this.updatedData.delivered === this.initData.delivered
            ? null : this.updatedData.delivered,
        },
      })
        .then(() => {
          this.$emit('update', {
            ...this.order,
            ...this.updatedData,
          });

          this.reset();
        })
        .finally(() => {
          this.pendingUpdate = false;
        });
    },
    cancelOrder() {
      this.pendingCancel = true;
      this.updateOrders({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          cancelledItems: [{
            day: this.order.day,
            payerId: this.order.payerId,
            availabilityId: this.order.availabilityId,
            count: this.order.count,
          }],
        },
      })
        .then(() => {
          this.$emit('refresh');
          this.$emit('close');
        })
        .finally(() => {
          this.pendingCancel = false;
        });
    },
    requestRemoveSubscription() {
      this.pendingCancel = true;
      this.removeSubscriptions({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: [{
            payerId: this.order.payerId,
            listingId: this.order.listingId,
          }],
        },
      })
        .then(() => {
          this.$emit('refresh');
          this.$emit('close');
        })
        .finally(() => {
          this.pendingCancel = false;
        });
    },
    formatDate(date) {
      return moment(date).format('DD MMM YYYY HH:mm');
    },
    fieldLabel(field) {
      const title = field?.title || field?.code || '-';
      return String(title).replace(/<[^>]*>/g, '').trim() || field?.code || '-';
    },
    fieldValue(field) {
      const value = this.subscriptionFields[field.code];
      if (value === undefined || value === null || value === '') {
        if (field.type === 'checkbox') return this.$t('general.no');
        return '-';
      }

      if (field.type === 'checkbox') {
        return value === true || value === 'true' || value === '1'
          ? this.$t('general.yes')
          : this.$t('general.no');
      }

      const option = (field.options || []).find((o) => `${o.value}` === `${value}`);
      if (option) return option.text;

      return value;
    },
    fileNameFromKey(key) {
      if (!key) return '';
      const parts = key.split('/');
      return parts[parts.length - 1] || key;
    },
    fieldAttachments(field) {
      const fieldAttachments = Array.isArray(field?.attachments) ? field.attachments : [];

      return fieldAttachments
        .map((attachment) => {
          const key = typeof attachment === 'string' ? attachment : attachment?.key;
          const matched = this.subscriptionAttachments.find((a) => a.key === key);

          return {
            key,
            url: matched?.url || '#',
            name: typeof attachment === 'string' ? this.fileNameFromKey(key) : attachment?.name,
          };
        })
        .filter((a) => !!a.key);
    },
    reset() {
      this.initData = {
        isPaid: this.order.isPaid,
        delivered: this.order.delivered > 0,
        deliveredCount: this.order.delivered,
        orderStatusId: this.order.orderStatusId || '00000000-0000-0000-0000-000000000000',
        unitPrice: this.order.unitPrice,
        comment: this.order.comment,
        discountName: this.order.discountName,
        count: this.order.count,
      };
      this.updatedData = { ...this.initData };
    },
  },
  created() {
    this.reset();
  },
};
</script>

<style lang="scss" scoped>
  .date {
    font-size: 12px;
  }

  .close {
    margin-top: 10px;
    font-size: 30px;
    position: absolute;
    right: 25px;
  }

  .set-row-hover:hover {
    background-color: rgba(100, 100, 100, 0.1);
  }

  .offer-name {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
  }

  .set-row {
    border-bottom: 1px solid #ddd;
    padding: 2px 2px;
    cursor: pointer;
    height: 32px;
    align-items: center;

    &.no-padding {
      padding: 0px;
    }

    &.checkbox {
      border-right: 1px solid rgba(100, 100, 100, 0.2);
      display: flex;
      justify-content: center;
    }

    &.selected {
      background-color: rgba($blue, 0.2);
    }
  }

  .payer-icon {
    color: $light-gray;
  }

  .money-input {
    &:deep(input) {
      font-size: 14px;
      border-radius: 0;
      border: 0;
      background-color: transparent;
      padding: 0;
      height: 25.6px;
      text-align: left;

      &:focus {
        box-shadow: none;
      }
    }

    &:deep(.input-group-text) {
      border: none;
      background-color: transparent;
      position: relative;
      top: -1px;
    }
  }

  .bill-button {
    width: 150px;
  }

  .form-value {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .field-attachment-link {
    width: fit-content;
  }
</style>
